/**
 * 이상한 방 - BGM 재생 엔진
 *
 * 근거 문서: 상태모델 7.0절(링크 하나로 즉시 접속) · 컨셉시트(관찰 게임)
 *
 * ## 한 판에 한 곡. 도중에 바뀌지 않는다
 *
 * 판이 시작될 때 곡 **하나**를 뽑아 그 판이 끝날 때까지 반복한다. 페이즈마다 곡을 바꾸는
 * 로테이션은 이 게임에서 해악이다 - **관찰 게임이라 곡이 계속 바뀌면 배경으로 물러나지
 * 않는다.** 외우기↔수색 전환에서도 바꾸지 않는다. 결과 화면까지 같은 곡으로 이어간다
 * (세션 = 한 판이므로 그쪽이 단순하고, 크로스페이드라는 장치 자체가 없어진다).
 *
 * 그래서 이 파일의 품질은 **루프 이음새 하나**로 결정된다. 한 판에서 같은 이음새를
 * 수십 번 듣기 때문이다. 다음 바퀴를 미리 예약해 앞 바퀴의 마지막 시각에 붙이므로
 * 정적이 생기지 않고, 마지막 음의 릴리스 꼬리가 다음 바퀴 위로 겹쳐 흐른다.
 *
 * ## 곡 하나만 받는다
 *
 * 16곡 합계가 140KB다. 전부 번들에 넣으면 첫 화면이 그만큼 늦어지는데, 한 판에 쓰는 것은
 * 그중 하나뿐이다. 그래서 곡 데이터는 `public/bgm/`에 두고 뽑힌 곡만 동적 import 한다
 * (한 판에 4~16KB). 실패해도 던지지 않는다 - **소리는 부가 기능이지 전제가 아니다.**
 *
 * ## 박 규격이 두 계열이다 (여기서 한 번만 처리한다)
 *
 * 파이프라인 10곡은 bpm이 사분음표 기준이고 시퀀스는 8분음표로 적혀 있다(`div: 2`).
 * 기존 편곡 6곡은 bpm이 세는 박과 시퀀스의 박이 같다(`div: 1`). 이 차이를 호출부가
 * 기억하는 구조로 두면 곡을 추가할 때마다 같은 실수가 재발한다 - 실제로 그렇게 해서
 * 파이프라인 10곡이 전부 두 배로 늘어졌던 적이 있다(빗방울 전주곡 2:32 → 5:04).
 * 그래서 `div`를 **곡 메타에 적고** `beatSeconds()`만 그것을 안다.
 *
 * ## 길이 값을 절대 건드리지 않는다
 *
 * `odd-moonlight`은 셋잇단 때문에 1/6박(0.6667) 길이가 섞여 있다. 재생 경로에서 반올림하거나
 * 0.25 배수로 강제하면 선율과 반주가 곡이 갈수록 벌어진다(실측 곡 끝에서 25초). `playSeq`는
 * 길이에 `beat`를 곱하기만 한다.
 *
 * ## 효과음과의 관계
 *
 * AudioContext는 `sharedAudioContext()` 하나를 나눠 쓴다(모바일에서 컨텍스트를 둘 만들면
 * 문제가 된다). 볼륨·음소거는 **따로다** - BGM은 배경이라 효과음보다 낮아야 하고,
 * 스트리머는 배경음만 끄고 싶어 한다. 저장 키도 `engine.ts`와 갈라 둔다.
 */

import { MS_PER_SECOND, SECONDS_PER_MINUTE } from '../core/time'
import type { Wave } from './engine'
import { clampUnit, readJsonSetting, writeJsonSetting } from './storage'

/* --- 곡 목록 --------------------------------------------------------------------- */

/**
 * 곡이 속한 풀.
 *
 * `play`  - 외우기·수색 중에 흐르는 곡. **관찰을 방해하지 않는 것**이 유일한 기준이다.
 * `bright` - 밝고 빠르거나 귀를 붙잡는 사건이 있는 곡. 결과·메뉴 쪽으로 돌린다.
 */
export type BgmPool = 'play' | 'bright'

export type BgmTrack = Readonly<{
  id: string
  /** `public/bgm/` 아래의 파일명. 뽑힌 곡만 이 이름으로 받는다. */
  file: string
  /**
   * 원곡 식별자. **최근 이력 비교는 id가 아니라 이것으로 한다.**
   * 짐노페디 1번이 두 파일로 들어 있어서, id로 비교하면 "같은 곡이 또 나왔다"가 된다.
   */
  work: string
  /** 개발자용 라벨(콘솔·테스트). **화면에 그리지 마라** - UI 문구는 `i18n`의 `t()`로 간다. */
  title: string
  bpm: number
  /** bpm이 세는 한 박을 시퀀스가 몇 박으로 쪼개 적었는가. 파이프라인 10곡은 8분음표라 2다. */
  div: 1 | 2
  pool: BgmPool
}>

/**
 * 채택 16곡. **곡을 추가할 자리는 여기 한 곳뿐이다.**
 *
 * 풀 배정 근거(곡 데이터를 실제로 재고 정했다):
 *  · `bright-*` 셋은 리빌/결과용으로 뽑힌 편곡이다. 특히 나흐트무지크는 사분음표 132로
 *    수색 내내 흐르면 재촉하는 소리가 된다. 엔터테이너는 래그타임 싱커페이션이 귀를 붙잡는다.
 *  · `radetzky`는 박수 트랙이 들어 있다. 점수판이 올라가는 화면의 곡이지 배경이 아니다.
 *  · `macabre`는 도입에 종을 열두 번 친다. 루프마다 그 열두 번이 되풀이되면 그건 배경이
 *    아니라 **사건**이다. 그래서 잔잔한 단조인데도 플레이 풀에 넣지 않았다.
 *  · 나머지 11곡은 밀도가 평탄하고 화성 변화가 느려서 귀가 붙잡을 사건이 없다.
 */
export const BGM_TRACKS: readonly BgmTrack[] = [
  /* 플레이 풀 - 외우기·수색·결과까지 이어서 흐른다 */
  { id: 'calm-gymnopedie-1', file: 'calm-gymnopedie-1.js', work: 'gymnopedie-1', title: '짐노페디 1번', bpm: 60, div: 2, pool: 'play' },
  { id: 'odd-gymnopedie-1', file: 'odd-gymnopedie-1.js', work: 'gymnopedie-1', title: '짐노페디 1번(다른 편곡)', bpm: 60, div: 2, pool: 'play' },
  { id: 'calm-raindrop', file: 'calm-raindrop.js', work: 'raindrop', title: '빗방울 전주곡', bpm: 63, div: 2, pool: 'play' },
  { id: 'calm-traumerei', file: 'calm-traumerei.js', work: 'traumerei', title: '트로이메라이', bpm: 50, div: 2, pool: 'play' },
  { id: 'calm-wtc1-prelude', file: 'calm-wtc1-prelude.js', work: 'wtc1-prelude', title: '평균율 1권 프렐류드', bpm: 60, div: 2, pool: 'play' },
  { id: 'odd-clair-de-lune', file: 'odd-clair-de-lune.js', work: 'clair-de-lune', title: '달빛', bpm: 56, div: 2, pool: 'play' },
  { id: 'odd-moonlight', file: 'odd-moonlight.js', work: 'moonlight', title: '월광 1악장', bpm: 60, div: 2, pool: 'play' },
  { id: 'aquarium', file: 'calm-aquarium.js', work: 'aquarium', title: '수족관', bpm: 156, div: 1, pool: 'play' },
  { id: 'promenade', file: 'core-promenade.js', work: 'promenade', title: '프롬나드', bpm: 92, div: 1, pool: 'play' },
  { id: 'gnossienne', file: 'core-gnossienne-1.js', work: 'gnossienne-1', title: 'Gnossienne No.1', bpm: 72, div: 1, pool: 'play' },
  { id: 'gnossienne3', file: 'odd-gnossienne-3.js', work: 'gnossienne-3', title: 'Gnossienne No.3', bpm: 63, div: 1, pool: 'play' },

  /* 밝은 풀 - 메뉴·결과 전용 */
  { id: 'bright-entertainer', file: 'bright-entertainer.js', work: 'entertainer', title: '엔터테이너', bpm: 72, div: 2, pool: 'bright' },
  { id: 'bright-nachtmusik', file: 'bright-nachtmusik.js', work: 'nachtmusik', title: '아이네 클라이네 나흐트무지크', bpm: 132, div: 2, pool: 'bright' },
  { id: 'bright-turkish-march', file: 'bright-turkish-march.js', work: 'turkish-march', title: '터키 행진곡', bpm: 72, div: 2, pool: 'bright' },
  { id: 'radetzky', file: 'bright-radetzky.js', work: 'radetzky', title: '라데츠키 행진곡', bpm: 116, div: 1, pool: 'bright' },
  { id: 'macabre', file: 'odd-macabre.js', work: 'macabre', title: '죽음의 무도', bpm: 126, div: 1, pool: 'bright' },
]

/** 1박의 초. **곡마다 다른 bpm 규격을 아는 유일한 함수다**(위 머리말의 박 규격 절 참고). */
export function beatSeconds(track: BgmTrack): number {
  return SECONDS_PER_MINUTE / (track.bpm * track.div)
}

/* --- 곡 모듈 계약 ---------------------------------------------------------------- */

export type NoteSeq = readonly (readonly [string | null, number])[]

export type VoiceOpts = Readonly<{
  wave?: Wave
  gain?: number
  attack?: number
  release?: number
  detune?: number
}>

export type PieceApi = Readonly<{
  playSeq(seq: NoteSeq, opts: VoiceOpts, startAt: number, beat: number): { nodes: OscillatorNode[]; end: number }
  N: Readonly<Record<string, number>>
}>

/** `public/bgm/*.js`가 default로 내보내는 것. `play`는 곡이 끝나는 시각을 돌려준다. */
export type PieceModule = Readonly<{ play(t0: number, beat: number, api: PieceApi): number }>

/** 곡 파일 하나를 받아 온다. 테스트가 가짜를 넣는 구멍이다. */
export type PieceLoader = (file: string) => Promise<PieceModule>

/* --- 상수 ------------------------------------------------------------------------ */

/** 저장 키. 효과음(`weird-room:audio`)과 **갈라 둔다** - 배경음만 끄는 선택이 가능해야 한다. */
export const BGM_STORAGE_KEY = 'weird-room:bgm'

/** 기본값은 **켬**이다. 끄는 선택은 저장되므로 스트리머는 한 번만 끄면 된다. */
const DEFAULT_MUTED = false

/** 배경음이므로 효과음(0.7)보다 확실히 낮게 시작한다. */
const DEFAULT_VOLUME = 0.38

/** 최근 이력에 남기는 **원곡** 수. 판을 이어서 하면 이만큼은 다른 곡이 나온다. */
const RECENT_WORKS = 4

/** 이력을 걷어낸 뒤에도 후보가 이만큼은 남아야 한다. 하나만 남으면 그건 랜덤이 아니다. */
const MIN_CANDIDATES = 2

/** 다음 바퀴를 몇 초 앞서 예약하나. 이 시간만큼만 두 바퀴의 노드가 함께 존재한다. */
const LOOP_LOOKAHEAD_SECONDS = 1.5

/** 예약 감시 주기(ms). 룩어헤드보다 충분히 짧으면 된다. */
const TICK_MS = 400

/** 첫 예약을 현재 시각에서 이만큼 뒤로 민다. 정각 예약은 첫 샘플이 잘린다. */
const SCHEDULE_LEAD_SECONDS = 0.02

/** 곡이 끝난 뒤 노드를 정리하기까지의 여유(초). 가장 긴 릴리스 꼬리보다 넉넉하다. */
const TAIL_SECONDS = 2

/** 음 하나가 소리 나는 비율. 나머지는 다음 음과의 틈이다(데모에서 승인된 값). */
const NOTE_SUSTAIN = 0.92

/**
 * 곡 파일이 음색 옵션을 생략했을 때의 값. **데모의 값을 그대로 옮겼다** - 개발자가 귀로
 * 고른 16곡이 그 소리로 승인됐으므로, 여기 숫자를 바꾸면 승인된 것과 다른 곡이 된다.
 */
const DEFAULT_NOTE_GAIN = 0.18
const DEFAULT_ATTACK_SECONDS = 0.01
const DEFAULT_RELEASE_SECONDS = 0.3

/** 릴리스가 끝난 뒤 오실레이터를 멈추기까지의 여유. 정각에 멈추면 꼬리가 잘려 딸깍 소리가 난다. */
const STOP_PAD_SECONDS = 0.05

/** 볼륨·정지 페이드(초). 즉시 끊으면 파형이 잘려 딸깍 소리가 난다. */
const GAIN_FADE_SECONDS = 0.04

/** `exponentialRampToValueAtTime`은 0으로 갈 수 없다. 사실상 무음인 값. */
const SILENCE = 0.0001

/** 어택 0은 같은 시각에 두 스케줄이 겹쳐 브라우저마다 결과가 갈린다. */
const MIN_ATTACK_SECONDS = 0.001

/**
 * 합성 상수를 한 묶음으로 내보낸다. **데모 페이지(`public/bgm-demo.html`)가 이 값을 받는다.**
 *
 * 데모는 곡을 지목해 이음새를 듣는 페이지라 스케줄러를 따로 짜지만, 음 하나가 울리는 모양은
 * 게임과 글자까지 같아야 한다 - 거기서 고른 소리가 곧 게임에서 나는 소리다. 한때 숫자를
 * 손으로 베껴 두었고 그것은 갈라질 자리였다. 이제 갈라질 자리가 없다.
 */
export const BGM_SYNTH = {
  noteSustain: NOTE_SUSTAIN,
  noteGain: DEFAULT_NOTE_GAIN,
  attackSeconds: DEFAULT_ATTACK_SECONDS,
  releaseSeconds: DEFAULT_RELEASE_SECONDS,
  minAttackSeconds: MIN_ATTACK_SECONDS,
  stopPadSeconds: STOP_PAD_SECONDS,
  silence: SILENCE,
  lookaheadSeconds: LOOP_LOOKAHEAD_SECONDS,
  tickMs: TICK_MS,
  leadSeconds: SCHEDULE_LEAD_SECONDS,
  tailSeconds: TAIL_SECONDS,
  fadeSeconds: GAIN_FADE_SECONDS,
} as const

/* --- 음이름 표 ------------------------------------------------------------------- */

const SEMITONES: readonly string[] = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B']
const FLAT_ALIAS: Readonly<Record<string, string>> = { Db: 'C#', Eb: 'D#', Gb: 'F#', Ab: 'G#', Bb: 'A#' }
const MIN_OCTAVE = 1
const MAX_OCTAVE = 7
const A4_HZ = 440
const A_INDEX = 9

/**
 * 음이름 → 주파수.
 *
 * 플랫 별칭이 **반드시** 있어야 한다. 표에 Bb/Eb가 없어 프롬나드가 통째로 무음이었던
 * 사고가 실제로 있었다. 16곡 전체를 이 표와 대조해 두었다(누락 0).
 */
function buildNoteTable(): Record<string, number> {
  const table: Record<string, number> = {}
  SEMITONES.forEach((name, i) => {
    for (let o = MIN_OCTAVE; o <= MAX_OCTAVE; o += 1) {
      table[name + String(o)] = A4_HZ * Math.pow(2, (i - A_INDEX) / 12 + (o - 4))
    }
  })
  for (const [flat, sharp] of Object.entries(FLAT_ALIAS)) {
    for (let o = MIN_OCTAVE; o <= MAX_OCTAVE; o += 1) {
      const hz = table[sharp + String(o)]
      if (hz !== undefined) table[flat + String(o)] = hz
    }
  }
  return table
}

export const NOTE_TABLE: Readonly<Record<string, number>> = buildNoteTable()

/* --- 설정 저장 -------------------------------------------------------------------- */

export type BgmSettings = Readonly<{ muted: boolean; volume: number; recent: readonly string[] }>

const DEFAULT_SETTINGS: BgmSettings = { muted: DEFAULT_MUTED, volume: DEFAULT_VOLUME, recent: [] }

/**
 * 저장소 배관(접근·파싱 예외를 삼키는 것)은 `storage.ts`가 맡는다. 여기는 **모양 검사와 기본값**만
 * 안다. `recent`의 검증은 이 모듈만의 것이라 여기 남는다 - 문자열이 아닌 항목은 조용히 버린다.
 */
function clampVolume(value: unknown): number {
  return clampUnit(value, DEFAULT_VOLUME)
}

export function readBgmSettings(): BgmSettings {
  const parsed = readJsonSetting(BGM_STORAGE_KEY)
  if (typeof parsed !== 'object' || parsed === null) return DEFAULT_SETTINGS
  const record = parsed as Readonly<Record<string, unknown>>
  const recent = record['recent']
  return {
    muted: typeof record['muted'] === 'boolean' ? record['muted'] : DEFAULT_MUTED,
    volume: clampVolume(record['volume']),
    recent: Array.isArray(recent) ? recent.filter((w): w is string => typeof w === 'string') : [],
  }
}

export function writeBgmSettings(settings: BgmSettings): void {
  writeJsonSetting(BGM_STORAGE_KEY, settings)
}

/* --- 곡 뽑기 ---------------------------------------------------------------------- */

/** 게임 상태가 아니므로 `Math.random()`을 써도 결정론 규칙에 걸리지 않는다. */
export type RandomSource = () => number

function pickFrom<T>(items: readonly T[], random: RandomSource): T | null {
  if (items.length === 0) return null
  const i = Math.min(items.length - 1, Math.max(0, Math.floor(random() * items.length)))
  return items[i] ?? null
}

/**
 * 풀에서 곡 하나를 뽑는다.
 *
 * **원곡 단위로 뽑는다.** id로 뽑으면 짐노페디 1번이 두 파일이라 다른 곡의 두 배 확률로
 * 걸리고, 최근 이력도 원곡으로 비교해야 "같은 곡이 또 나왔다"를 막을 수 있다.
 * 이력을 다 걷어내면 후보가 하나만 남는 풀이 있으므로(밝은 풀은 원곡 5개다),
 * 후보가 둘 이상 남는 **가장 강한 배제**를 골라 쓴다.
 */
export function pickTrack(pool: BgmPool, recent: readonly string[], random: RandomSource = Math.random): BgmTrack | null {
  const tracks = BGM_TRACKS.filter((t) => t.pool === pool)
  if (tracks.length === 0) return null

  const works = [...new Set(tracks.map((t) => t.work))]
  let allowed = works
  for (let keep = Math.min(recent.length, RECENT_WORKS); keep >= 0; keep -= 1) {
    const banned = new Set(recent.slice(0, keep))
    const rest = works.filter((w) => !banned.has(w))
    if (rest.length >= Math.min(MIN_CANDIDATES, works.length)) {
      allowed = rest
      break
    }
  }

  const work = pickFrom(allowed, random)
  if (work === null) return null
  return pickFrom(tracks.filter((t) => t.work === work), random)
}

/* --- AudioContext 공유 ------------------------------------------------------------ */

export type AudioContextFactory = () => AudioContext

let sharedContext: AudioContext | null = null

/**
 * 효과음 엔진과 **같은** AudioContext.
 *
 * 모바일에서 컨텍스트를 둘 만들면 자동재생 해제가 한쪽에만 걸리고 지연이 늘어난다.
 * `engine.ts`의 싱글턴(`audio`)이 이 함수를 `contextFactory`로 받으므로 앱 전체에 컨텍스트는 하나다.
 * 이쪽은 `engine.ts`에서 타입만 가져오므로(`import type`) 그 방향은 순환이 아니다.
 */
export function sharedAudioContext(): AudioContext {
  if (sharedContext !== null) return sharedContext
  const scope = globalThis as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext }
  const Ctor = scope.AudioContext ?? scope.webkitAudioContext
  if (Ctor === undefined) throw new Error('AudioContext 없음')
  sharedContext = new Ctor()
  return sharedContext
}

/* --- 재생기 ----------------------------------------------------------------------- */

export type BgmPlayerOptions = Readonly<{
  contextFactory?: AudioContextFactory
  loader?: PieceLoader
  settings?: BgmSettings
  /** 설정을 저장할지. 테스트는 끈다. */
  persist?: boolean
  random?: RandomSource
}>

export interface BgmPlayer {
  /** 첫 사용자 제스처 안에서 부른다. AudioContext는 **여기서 처음 생긴다.** */
  unlock(): void
  /**
   * 한 판을 시작한다. 곡 하나를 뽑아 그 판이 끝날 때까지 반복한다.
   * 이미 돌고 있어도 **새로 뽑는다** - 재도전은 같은 방을 다시 보는 것이라 같은 곡이면 지루하다.
   */
  start(pool?: BgmPool): BgmTrack | null
  /**
   * 이 풀의 곡을 **이어 간다.** 이미 이 풀의 곡을 쥐고 있으면 아무 일도 하지 않고,
   * 아니면 새로 뽑는다(`start`와 정확히 반대되는 계약이다).
   *
   * 여러 경로로 들어오는 화면이 쓰는 입구다 - 메뉴는 타이틀에서도 오고 판을 끝내고도 온다.
   * 앞쪽 경로에서 `start`를 그대로 부르면 **방금 튼 곡을 곧바로 다시 뽑아 끊는다.**
   *
   * 판단을 여기 두는 이유는 `isPlaying()`이 이 질문에 답하지 못하기 때문이다 - 곡 파일을
   * 받는 동안(동적 import)에는 노드가 하나도 없어 `false`이고, 음소거일 때도 `false`다.
   * 그 둘을 "안 돌고 있다"로 읽으면 곡을 받는 사이에 들어온 두 번째 호출이 로드를 버리고
   * 처음부터 다시 시킨다. 곡을 쥐었는가(`track`)가 이 질문의 진실이고, 그 값은 뽑는 순간
   * 동기적으로 정해진다.
   *
   * 로드에 실패해 소리가 없는 경우에도 이어 간 것으로 본다 - 다시 뽑아 봐야 같은 실패가
   * 반복될 뿐이고, 그 재시도가 화면 전환마다 도는 경로에 들어가면 그게 사고다.
   */
  ensurePool(pool: BgmPool): BgmTrack | null
  /** 지금 도는 곡. 판 도중에는 절대 바뀌지 않는다. */
  current(): BgmTrack | null
  /** 실제로 노드가 예약되어 있는가. 음소거·미해제·로드 실패는 전부 `false`다. */
  isPlaying(): boolean
  stop(): void
  isMuted(): boolean
  setMuted(muted: boolean): void
  /** 바뀐 뒤의 음소거 상태를 돌려준다. HUD 버튼이 이것만 부르면 된다. */
  toggleMute(): boolean
  getVolume(): number
  setVolume(volume: number): void
  /** 최근에 나온 원곡들(새것부터). 진단·테스트용. */
  recentWorks(): readonly string[]
  /** 진행 중인 로드가 끝날 때까지 기다린다. **테스트·진단 전용.** */
  whenSettled(): Promise<void>
  dispose(): void
}

/** 한 바퀴가 만든 노드와 끝나는 시각. 정지·정리가 이것만 보면 된다. */
type Lap = Readonly<{ nodes: readonly AudioNode[]; sources: readonly OscillatorNode[]; end: number }>

/** 예약이 진행되는 동안의 바퀴. `playSeq`가 여기에 노드를 쌓는다. */
type MutableLap = { nodes: AudioNode[]; sources: OscillatorNode[]; end: number }

function defaultLoader(file: string): Promise<PieceModule> {
  const base = import.meta.env.BASE_URL || '/'
  return import(/* @vite-ignore */ `${base}bgm/${file}`).then(
    (mod: { default?: PieceModule }) => {
      const piece = mod.default
      if (piece === undefined || typeof piece.play !== 'function') throw new Error(`곡 형식이 아니다: ${file}`)
      return piece
    },
  )
}

export function createBgmPlayer(options: BgmPlayerOptions = {}): BgmPlayer {
  const factory = options.contextFactory ?? sharedAudioContext
  const load = options.loader ?? defaultLoader
  const random = options.random ?? Math.random
  const persist = options.persist ?? true

  const initial = options.settings ?? (persist ? readBgmSettings() : DEFAULT_SETTINGS)
  let muted = initial.muted
  let volume = clampVolume(initial.volume)
  let recent: string[] = [...initial.recent]

  let context: AudioContext | null = null
  let bus: GainNode | null = null
  let broken = false
  let unlocked = false

  let track: BgmTrack | null = null
  let piece: PieceModule | null = null
  /** 로드·예약이 어느 판의 것인지. `start()`/`stop()`마다 올려 낡은 로드를 버린다. */
  let generation = 0
  let settling: Promise<void> = Promise.resolve()

  let laps: Lap[] = []
  let nextStart = 0
  let ticker: ReturnType<typeof setInterval> | null = null

  /* --- 노드 --------------------------------------------------------------------- */

  function ensureContext(): AudioContext | null {
    if (context !== null) return context
    if (broken) return null
    try {
      const ctx = factory()
      const gain = ctx.createGain()
      gain.gain.value = muted ? 0 : volume
      gain.connect(ctx.destination)
      context = ctx
      bus = gain
      return ctx
    } catch {
      // 구형 브라우저·정책 차단. 배경음은 없지만 게임은 계속된다.
      broken = true
      return null
    }
  }

  function fadeBusTo(value: number): void {
    const ctx = context
    const gain = bus
    if (ctx === null || gain === null) return
    try {
      const now = ctx.currentTime
      gain.gain.cancelScheduledValues(now)
      gain.gain.setValueAtTime(gain.gain.value, now)
      gain.gain.linearRampToValueAtTime(value, now + GAIN_FADE_SECONDS)
    } catch {
      gain.gain.value = value
    }
  }

  /** 표에 없는 음이름은 **조용히 넘기지 않는다.** 조용히 넘기면 다음에 또 못 찾는다. */
  const reported = new Set<string>()
  function frequency(note: string): number | null {
    const hz = NOTE_TABLE[note]
    if (hz === undefined) {
      if (!reported.has(note)) {
        reported.add(note)
        console.error('[BGM] 표에 없는 음이름 · 이 음은 무음이 된다:', note)
      }
      return null
    }
    return hz
  }

  function makeApi(ctx: AudioContext, target: GainNode, lap: MutableLap): PieceApi {
    return {
      N: NOTE_TABLE,
      playSeq(seq, opts, startAt, beat) {
        const made: OscillatorNode[] = []
        let t = startAt
        for (const event of seq) {
          const note = event[0]
          // 길이는 곱하기만 한다. 반올림·격자 강제 금지(머리말의 셋잇단 절 참고).
          const span = event[1] * beat
          const hz = note === null ? null : frequency(note)
          if (hz !== null) {
            const osc = ctx.createOscillator()
            const env = ctx.createGain()
            const gain = opts.gain ?? DEFAULT_NOTE_GAIN
            const attack = Math.max(MIN_ATTACK_SECONDS, opts.attack ?? DEFAULT_ATTACK_SECONDS)
            const release = opts.release ?? DEFAULT_RELEASE_SECONDS
            const dur = span * NOTE_SUSTAIN

            osc.type = opts.wave ?? 'sine'
            osc.frequency.value = hz
            osc.detune.value = opts.detune ?? 0
            env.gain.setValueAtTime(SILENCE, t)
            env.gain.linearRampToValueAtTime(gain, t + attack)
            env.gain.exponentialRampToValueAtTime(SILENCE, t + dur + release)
            osc.connect(env)
            env.connect(target)
            osc.start(t)
            osc.stop(t + dur + release + STOP_PAD_SECONDS)

            lap.nodes.push(osc, env)
            lap.sources.push(osc)
            made.push(osc)
          }
          t += span
        }
        return { nodes: made, end: t }
      },
    }
  }

  function disconnectLap(lap: Lap): void {
    for (const node of lap.nodes) {
      try {
        node.disconnect()
      } catch {
        // 이미 끊긴 노드. 정리가 목적이므로 무시한다.
      }
    }
  }

  /**
   * 아직 울리고 있는 바퀴를 끈다.
   *
   * 오실레이터를 **버스 페이드가 끝나는 시각에** 멈춘다. 지금 당장 멈추면 파형이 잘려
   * 딸깍 소리가 나고, 페이드를 걸어 둔 의미도 없어진다.
   */
  function killLap(lap: Lap, ctx: AudioContext): void {
    const at = ctx.currentTime + GAIN_FADE_SECONDS
    for (const source of lap.sources) {
      try {
        source.stop(at)
      } catch {
        // 이미 끝난 노드는 던진다.
      }
    }
    setTimeout(() => disconnectLap(lap), (GAIN_FADE_SECONDS + STOP_PAD_SECONDS) * MS_PER_SECOND)
  }

  /* --- 루프 --------------------------------------------------------------------- */

  /**
   * 다음 한 바퀴를 예약한다.
   *
   * 시작 시각을 **앞 바퀴의 끝 시각 그대로** 쓰는 것이 이 함수의 전부다. 그래야 이음새에
   * 정적이 생기지 않고, 앞 바퀴 마지막 음의 릴리스 꼬리가 다음 바퀴 위로 자연스럽게 겹친다.
   */
  function scheduleLap(): boolean {
    const ctx = context
    const target = bus
    if (ctx === null || target === null || piece === null || track === null) return false

    const t0 = Math.max(nextStart, ctx.currentTime + SCHEDULE_LEAD_SECONDS)
    const lap: MutableLap = { nodes: [], sources: [], end: t0 }
    let end: number
    try {
      end = piece.play(t0, beatSeconds(track), makeApi(ctx, target, lap))
    } catch (error) {
      console.error('[BGM] 곡 재생에 실패했다 · 배경음 없이 계속한다:', track.id, error)
      killLap(lap, ctx)
      return false
    }
    // 길이 0인 곡을 무한히 예약하면 그건 소리가 아니라 멈춤이다.
    if (!Number.isFinite(end) || end <= t0) {
      console.error('[BGM] 곡 길이가 0이다 · 루프를 멈춘다:', track.id)
      killLap(lap, ctx)
      return false
    }

    laps.push({ nodes: lap.nodes, sources: lap.sources, end })
    nextStart = end
    return true
  }

  function tick(): void {
    const ctx = context
    if (ctx === null) return
    const now = ctx.currentTime
    if (now > nextStart - LOOP_LOOKAHEAD_SECONDS && !scheduleLap()) {
      stopPlayback()
      return
    }
    const finished = laps.filter((lap) => now > lap.end + TAIL_SECONDS)
    if (finished.length > 0) {
      laps = laps.filter((lap) => !finished.includes(lap))
      for (const lap of finished) disconnectLap(lap)
    }
  }

  function startPlayback(): void {
    if (ticker !== null) return
    if (muted || !unlocked || piece === null || track === null) return
    const ctx = ensureContext()
    if (ctx === null) return

    fadeBusTo(volume)
    nextStart = 0
    if (!scheduleLap()) return
    ticker = setInterval(tick, TICK_MS)
  }

  function stopPlayback(): void {
    if (ticker !== null) {
      clearInterval(ticker)
      ticker = null
    }
    fadeBusTo(0)
    const ctx = context
    if (ctx !== null) for (const lap of laps) killLap(lap, ctx)
    laps = []
    nextStart = 0
  }

  /** 뽑힌 곡을 받아 온다. **여기서는 어떤 오디오 노드도 만들지 않는다.** */
  function fetchPiece(): void {
    const mine = generation
    const wanted = track?.file
    if (wanted === undefined) return
    settling = load(wanted)
      .then((mod) => {
        if (generation !== mine) return
        piece = mod
        startPlayback()
      })
      .catch((error: unknown) => {
        if (generation !== mine) return
        // 게임은 정상 동작해야 한다. 배경음만 없는 상태로 남는다.
        console.warn('[BGM] 곡을 받지 못했다 · 배경음 없이 계속한다:', wanted, error)
        piece = null
      })
  }

  /** 소리를 낼 수 있게 됐을 때(해제·음소거 해제·곡 도착) 부르는 한 곳. */
  function resume(): void {
    if (muted || track === null) return
    if (piece === null) fetchPiece()
    else startPlayback()
  }

  function save(): void {
    if (persist) writeBgmSettings({ muted, volume, recent })
  }

  function unlock(): void {
    const ctx = ensureContext()
    if (ctx === null) return
    unlocked = true
    try {
      void ctx.resume().catch(() => undefined)
    } catch {
      // resume이 동기로 던지는 구형 구현이 있다.
    }
    resume()
  }

  function start(pool: BgmPool = 'play'): BgmTrack | null {
    generation += 1
    stopPlayback()
    piece = null
    const picked = pickTrack(pool, recent, random)
    track = picked
    if (picked === null) return null
    recent = [picked.work, ...recent.filter((w) => w !== picked.work)].slice(0, RECENT_WORKS)
    save()
    resume()
    return picked
  }

  /** 계약은 인터페이스 쪽 주석에. 판단 근거가 `track` 하나인 것이 요점이다. */
  function ensurePool(pool: BgmPool): BgmTrack | null {
    if (track !== null && track.pool === pool) return track
    return start(pool)
  }

  function stop(): void {
    generation += 1
    stopPlayback()
    piece = null
    track = null
  }

  function setMuted(value: boolean): void {
    if (muted === value) return
    muted = value
    save()
    // 음소거면 노드를 아예 만들지 않는다. 게인만 0으로 두고 계속 예약하는 것은 낭비다.
    if (muted) stopPlayback()
    else resume()
  }

  return {
    unlock,
    start,
    ensurePool,
    stop,
    setMuted,
    current: () => track,
    isPlaying: () => ticker !== null,
    isMuted: () => muted,
    toggleMute(): boolean {
      setMuted(!muted)
      return muted
    },
    getVolume: () => volume,
    setVolume(value: number): void {
      volume = clampVolume(value)
      save()
      if (!muted) fadeBusTo(volume)
    },
    recentWorks: () => recent,
    whenSettled: () => settling,
    dispose(): void {
      generation += 1
      stopPlayback()
      piece = null
      track = null
      try {
        bus?.disconnect()
      } catch {
        // 이미 끊긴 노드.
      }
      bus = null
      // AudioContext는 효과음과 공유하므로 **닫지 않는다.**
      context = null
      unlocked = false
    },
  }
}

/** 앱이 쓰는 싱글턴. 테스트는 `createBgmPlayer()`로 자기 것을 만든다. */
export const bgm: BgmPlayer = createBgmPlayer()
